import { EventMapPoint } from './types';
import { countryOfBundesland, expandEventTypesWithChildren } from './brand';

/**
 * Die Filter der Ergebnisliste, soweit sie sich auf einem Kartenpunkt nachrechnen lassen.
 *
 * Warum überhaupt clientseitig: die Umkreis-Karte zeigt *alle* kommenden Events und beziffert
 * live, wie viele im Radius liegen. Diese Zahl steht auf dem CTA („N Events anzeigen") — sie muss
 * also dieselbe Menge treffen wie die Liste danach, sonst verspricht der Knopf etwas, das die
 * Seite nicht einlöst. Deshalb prüft diese Funktion dieselben Bedingungen wie EventRepository.
 *
 * Nicht abgebildet sind Freitextsuche und Stadt — beide laufen über Spalten, die nicht in der
 * Kartennutzlast stehen. Sie sind bewusst draußen: wer im Suchfeld tippt, wählt keinen Umkreis.
 */
export interface MapPointFilters {
  /** Leit- **oder** Nebenkategorie, genau wie serverseitig. */
  category?: string | null;
  bundesland?: string | null;
  /** Ländercode (DE/AT/CH/…) — serverseitig ein `bundesland IN (…)`. */
  country?: string | null;
  /** ISO-Datum: Events **ab** diesem Tag. */
  date?: string | null;
  /** ISO-Datum: Events, die **spätestens** an diesem Tag beginnen (Ende des Zeitraums). */
  dateTo?: string | null;
  minKm?: number | null;
  maxKm?: number | null;
  /** Mehrfachauswahl wie im Web; leer/undefined = alle Eventarten. */
  eventTypes?: string[] | null;
  /** Auch permanente Events in Saisonpause — serverseitig `includeOffSeason`. */
  includeOffSeason?: boolean;
}

export function matchesMapPointFilters(p: EventMapPoint, f: MapPointFilters): boolean {
  if (f.category && p.categories.indexOf(f.category as EventMapPoint['categories'][number]) < 0) return false;
  if (f.bundesland && p.bundesland !== f.bundesland) return false;
  if (f.country && countryOfBundesland(p.bundesland) !== f.country) return false;
  // Ein permanentes Event hat keinen Termin — sein Datum ist ein Platzhalter und filtert nicht.
  if (!p.permanent) {
    if (f.date && p.eventDate < f.date) return false;
    if (f.dateTo && p.eventDate > f.dateTo) return false;
  }
  // Die Saisonpause ist für ein permanentes Event, was für einen Termin „vorbei" ist.
  if (p.offSeason && !f.includeOffSeason) return false;
  if (f.eventTypes && f.eventTypes.length > 0) {
    // Mit den Unterarten, genau wie serverseitig: „RTF" schließt Radmarathons ein. Und mit den
    // Arten der Strecken: ein Event mit einer Gravel-Race-Strecke zählt unter „Gravel-Race".
    const wanted = expandEventTypesWithChildren(f.eventTypes);
    const own = [p.eventType, ...(p.distanceEventTypes || [])];
    if (!own.some(t => !!t && wanted.indexOf(t) >= 0)) return false;
  }
  if (f.minKm != null || f.maxKm != null) {
    // Eine **einzelne** Strecke muss beide Grenzen erfüllen (serverseitig ein EXISTS über
    // event_distances). Ein Event mit 50 und 200 km fällt bei „100–150 km" deshalb heraus.
    const distances = p.distances || [];
    const hit = distances.some(km =>
      (f.minKm == null || km >= f.minKm) && (f.maxKm == null || km <= f.maxKm)
    );
    if (!hit) return false;
  }
  return true;
}

export function filterMapPoints(points: EventMapPoint[], f: MapPointFilters): EventMapPoint[] {
  return points.filter(p => matchesMapPointFilters(p, f));
}
